import React, { useCallback, useEffect, useState } from "react";
import API from "../../Utils/api";
import { CHECK_BALANCE_PARAMS } from "../../Utils/Constants";

// 9xTrader provider (CHECK_BALANCE_PARAMS = "9XCheckBalance"). ARC users never
// render this card, so their screens stay unchanged.
const NINE_X_CHECK_BALANCE_PARAMS = "9XCheckBalance";

export const isNineXTradingUser = (user) =>
  CHECK_BALANCE_PARAMS === NINE_X_CHECK_BALANCE_PARAMS &&
  Number(user?.role) === 7 &&
  Boolean(user?.nineXUserData?.tradingAccountId);

const money = (value) =>
  Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const labelStyle = { margin: "0 0 4px 0", fontSize: "12px", color: "#718096" };
const valueStyle = { margin: 0, fontSize: "14px", fontWeight: "600", color: "#2d3748" };

/**
 * Live 9xTrader trading-account details for one user (balance, credit, equity,
 * free margin …), fetched from the admin backend on mount and on refresh.
 */
export const NineXTradingAccountCard = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!isNineXTradingUser(user)) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await API.get(
        `/trading-account-details?userId=${user._id}&paramsType=${CHECK_BALANCE_PARAMS}`
      );
      if (res?.meta?.code === 200) setData(res.data);
      else setError(res?.meta?.message || "Unable to load the trading account.");
    } catch (err) {
      setError(err?.response?.data?.meta?.message || "Unable to load the trading account.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  if (!isNineXTradingUser(user)) return null;

  const account = data?.tradingAccount;
  const currency = account?.currency || user?.currency || "USD";
  const rows = [
    { label: "Trading Balance", value: `${money(data?.mt5balance)} ${currency}`, highlight: true },
    { label: "Credit/Bonus Balance", value: `${money(data?.totalbalance)} ${currency}` },
    { label: "Equity", value: `${money(account?.equity)} ${currency}` },
    { label: "Free Margin", value: `${money(account?.freeMargin)} ${currency}` },
    { label: "Login", value: account?.login ?? user?.nineXUserData?.login ?? "—" },
    { label: "Leverage", value: account?.leverage ? `1:${account.leverage}` : "—" },
  ];

  return (
    <div
      style={{
        marginTop: "12px",
        padding: "12px 16px",
        borderRadius: "8px",
        border: "1px solid #e2e8f0",
        background: "#f7fafc",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
        <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#2d3748" }}>9xTrader Trading Account</p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            border: "none",
            background: "transparent",
            color: "#3182ce",
            fontSize: "12px",
            fontWeight: "600",
            cursor: loading ? "default" : "pointer",
          }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <p style={{ margin: 0, fontSize: "13px", color: "#e53e3e" }}>{error}</p>
      ) : !data && loading ? (
        <p style={{ margin: 0, fontSize: "13px", color: "#718096" }}>Loading trading account…</p>
      ) : data ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          {rows.map((row) => (
            <div key={row.label}>
              <p style={labelStyle}>{row.label}</p>
              <p style={row.highlight ? { ...valueStyle, fontSize: "16px", color: "#3182ce" } : valueStyle}>
                {row.value}
              </p>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
